import { useEffect, useState } from "react";

import { getAllGisLayers } from "services/requests/v1/gislayer";

import {
    GisLayerTableType,
    ModalContentType,
    SetRequestError,
    SubscribeContentType,
    UnsubscribeContentType,
} from "types/generic";

import GoAiguaButton from "components/generic/goaiguabutton";
import ConfirmationDeleteModal from "components/generic/confirmationdeletemodal";
import CreateNewLayer from "components/adhoc/createnewlayergis";
import GoAiguaModal from "components/generic/goaiguamodal";
import GisLayerTable from "./gislayertable";
import { SearchBox } from "components/generic";
import { useTranslation } from "utils/hooks/use-translation.hook";
import { makeGisLayerMasterWidgetFunctions } from "utils/components/gis-layer-master-widget-functions";

type GisLayerTableParams = {
    rowSelected: GisLayerTableType[];
    rowSelectedWithId: GisLayerTableType[];
    rowData: GisLayerTableType[];
    contentName: string;
    setRowSelected: (rowSelected: GisLayerTableType[]) => void;
    setRowSelectedWithId: (rowSelectedWithId: GisLayerTableType[]) => void;
    setRowData: (rowSelected: GisLayerTableType[]) => void;
    subscribeContent: SubscribeContentType;
    unsubscribeContent: UnsubscribeContentType;
    setError: SetRequestError;
};

const GisLayerMasterLayersWidget = ({
    rowSelected,
    rowData,
    rowSelectedWithId,
    contentName,
    setRowSelected,
    setRowSelectedWithId,
    setRowData,
    setError,
    subscribeContent,
    unsubscribeContent,
}: GisLayerTableParams) => {
    const translate = useTranslation();

    const [textFilter, setTextFilter] = useState<string>("");
    const [layerId, setLayerId] = useState<number>(0);
    const [modalContent, setModalContent] = useState<ModalContentType>(null);

    const [rowDataFiltered, setRowDataFiltered] = useState<GisLayerTableType[]>(
        []
    );

    const layerData = rowData.find(({ id }) => id === layerId);

    const {
        closeModal,
        defineModalTitle,
        openCreateLayerModal,
        openDeleteLayerModal,
    } = makeGisLayerMasterWidgetFunctions({
        modalContent,
        setModalContent,
        translate,
    });

    useEffect(() => {
        subscribeContent(contentName);

        getAllGisLayers()
            .then((res) => {
                setRowData(res.data);
                unsubscribeContent(contentName);
            })
            .catch(() => setError(contentName));
    }, []);

    useEffect(() => {
        if (textFilter === "") {
            setRowDataFiltered(rowData);
        } else {
            let result = rowData.filter((params) => {
                if (
                    params.alias
                        .toLowerCase()
                        .includes(textFilter.toLowerCase())
                ) {
                    return params;
                }
            });
            setRowDataFiltered(result);
        }
    }, [textFilter, rowData]);

    return (
        <>
            <div className="c-layer-table__header">
                <div className="c-layer-table__header__title">
                    {translate("app.gis.layers")}
                </div>
            </div>
            <div className="c-layer-table">
                <div className="c-layer-table__utils">
                    <SearchBox
                        placeholder={translate("app.hierarchy.search")}
                        value={textFilter}
                        className="c-layer-table__utils__search"
                        onChange={(value) => setTextFilter(value)}
                    />
                    <GoAiguaButton
                        className="c-layer-table__utils__button"
                        text={translate("app.gis.create_new_layer")}
                        onClick={openCreateLayerModal}
                    />
                </div>
                <div className="c-layer-table__grid">
                    <GisLayerTable
                        rowData={rowDataFiltered}
                        rowSelected={rowSelected}
                        setRowSelected={setRowSelected}
                        rowSelectedWithId={rowSelectedWithId}
                        setRowSelectedWithId={setRowSelectedWithId}
                        setIsOpen={openDeleteLayerModal}
                        setLayerId={setLayerId}
                    />
                </div>
            </div>
            <GoAiguaModal
                headerTitle={defineModalTitle()}
                isOpen={modalContent !== null}
                setIsOpen={closeModal}
                className="c-create-new-layer"
            >
                {modalContent === "create" && (
                    <CreateNewLayer
                        setIsOpen={closeModal}
                        setRowData={setRowData}
                        subscribeContent={subscribeContent}
                        unsubscribeContent={unsubscribeContent}
                        setError={setError}
                        contentName={contentName}
                    />
                )}

                {modalContent === "delete" && (
                    <ConfirmationDeleteModal
                        layerId={layerId}
                        layerName={layerData?.alias || ""}
                        closeModal={closeModal}
                        setRowData={setRowData}
                        subscribeContent={subscribeContent}
                        unsubscribeContent={unsubscribeContent}
                        setError={setError}
                    />
                )}
            </GoAiguaModal>
        </>
    );
};

export default GisLayerMasterLayersWidget;
